import { useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import AppointmentList from "../components/Calendar/AppointmentList";
import Calendar from "../components/Calendar/Calendar";
import {
  getAppointmentsByDateRange,
  getCalendarSummary,
  patchAppointment,
  saveAppointment,
} from "../api/appointmentApi";
import { DateTime } from "luxon";
import { PlusIcon } from "@heroicons/react/20/solid";
import Modal from "../components/Shared/Modal";
import AppointmentForm from "../components/Appointment/AppointmentForm";
import ViewAppointment from "../components/Appointment/ViewAppointment";
import CancelAppointmentForm from "../components/Appointment/CancelAppointmentForm";
import { FORM_MODE } from "../config/constants";
import { useToast } from "../hooks/useToast";
import { useUserPreferences } from "../hooks/useUserPreferences";
import { formatPriceWithCurrency, parseDecimal128 } from "../utility/priceUtils";
import EmptyState from "../components/Shared/EmptyState";
import useCreateIntent from "../hooks/useCreateIntent";

const CalendarPage = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [showAppointmentDrawer, setShowAppointmentDrawer] = useState(false);
  const [showAppointmentView, setShowAppointmentView] = useState(false);
  const [showCancelDrawer, setShowCancelDrawer] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [appointmentFormMode, setAppointmentFormMode] = useState(null);
  const [appointmentModalTitle, setAppointmentModalTitle] = useState("Create Appointment");
  const [isFormDirty, setIsFormDirty] = useState(false);
  const modalCloseRef = useRef(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { currency } = useUserPreferences();

  const { data: appointmentsData, isLoading: isLoadingAppointments } = useQuery(
    {
      queryKey: [
        "appointments",
        {
          start: DateTime.fromJSDate(selectedDate).startOf('day').toISO(),
          end: DateTime.fromJSDate(selectedDate).endOf('day').toISO(),
          page: 1,
          limit: 50,
        },
      ],
      queryFn: ({ queryKey }) => {
        const [, params] = queryKey;
        return getAppointmentsByDateRange(params);
      },
      placeholderData: keepPreviousData,
    }
  );

  const calendarMonthDt = useMemo(() => DateTime.fromJSDate(calendarMonth), [calendarMonth]);
  const systemTimezone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);
  const { data: calendarSummary } = useQuery({
    queryKey: [
      "calendarSummary",
      {
        start: calendarMonthDt.startOf("month").startOf("week").toISO(),
        end: calendarMonthDt.endOf("month").endOf("week").toISO(),
        timezone: systemTimezone,
      },
    ],
    queryFn: ({ queryKey }) => {
      const [, params] = queryKey;
      return getCalendarSummary(params);
    },
  });

  // Form-driven patch: awaited, errors handled inside AppointmentForm /
  // CancelAppointmentForm via mapApiError.
  const { mutateAsync: mutatePatchAppointment } = useMutation({
    mutationFn: patchAppointment,
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: ["appointments"] });
      const previousAppointmentQueries = queryClient.getQueriesData({ queryKey: ["appointments"] });
      const newStatus = variables.data?.status;
      if (newStatus) {
        previousAppointmentQueries.forEach(([queryKey]) => {
          queryClient.setQueryData(queryKey, (old) => {
            if (!old?.appointments) return old;
            return {
              ...old,
              appointments: old.appointments.map((apt) =>
                apt.id === variables.id ? { ...apt, status: newStatus } : apt
              ),
            };
          });
        });
      }
      return { previousAppointmentQueries };
    },
    onSuccess: (data, variables) => {
      if (variables.data?.status === "cancelled") {
        toast.warning("Appointment Cancelled", "Appointment has been cancelled successfully");
      } else {
        toast.success("Appointment Updated", "Appointment has been updated successfully");
      }
      closeDrawerHandler();
    },
    onError: (_err, _variables, context) => {
      // Rollback optimistic state; the form surfaces the user-facing error.
      if (context?.previousAppointmentQueries) {
        context.previousAppointmentQueries.forEach(([queryKey, data]) => {
          if (data !== undefined) queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["appointments"] });
      queryClient.invalidateQueries({ queryKey: ["calendarSummary"] });
    },
  });

  const { mutateAsync: mutateSaveAppointment } = useMutation({
    mutationFn: saveAppointment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["appointments"] });
      queryClient.invalidateQueries({ queryKey: ["calendarSummary"] });
      toast.success(
        "Appointment Created",
        "New appointment has been scheduled successfully"
      );
      closeDrawerHandler();
    },
  });

  const closeDrawerHandler = () => {
    setShowAppointmentDrawer(false);
    setShowAppointmentView(false);
    setShowCancelDrawer(false);
    setSelectedAppointment(null);
    setAppointmentFormMode(null);
    setIsFormDirty(false);
  };

  const onViewHandler = (appointment) => {
    setShowAppointmentView(true);
    setSelectedAppointment(appointment);
  };

  const onEditHandler = (appointment) => {
    setShowAppointmentDrawer(true);
    setAppointmentFormMode(FORM_MODE.UPDATE);
    setAppointmentModalTitle("Edit Appointment");
    setSelectedAppointment(appointment);
  };

  const onCancelHandler = (appointment) => {
    setShowCancelDrawer(true);
    setSelectedAppointment(appointment);
  };

  const onClientClickHandler = (clientId) => {
    if (clientId) navigate(`/clients/${clientId}`);
  };

  const onCreateHandler = () => {
    setShowAppointmentDrawer(true);
    setAppointmentFormMode(FORM_MODE.CREATE);
    setAppointmentModalTitle("Create Appointment");
    setSelectedAppointment(null);
  };

  // Mobile bottom-nav "+" deep-links here with ?new=appointment
  useCreateIntent("appointment", onCreateHandler);

  const getScheduleHeading = () => {
    const dt = DateTime.fromJSDate(selectedDate);
    const now = DateTime.now();
    if (dt.hasSame(now, "day")) {
      return `Today's schedule \u2014 ${dt.toFormat("MMM d, yyyy")}`;
    }
    return `Schedule for ${dt.toFormat("EEEE, MMM d, yyyy")}`;
  };

  const appointments = appointmentsData?.appointments || [];
  const appointmentCount = appointments.length;
  const dailyRevenue = appointments.reduce((sum, a) => {
    if (a.status === "completed" || a.status === "confirmed") {
      return sum + (parseDecimal128(a.totalPrice) || 0);
    }
    return sum;
  }, 0);

  return (
    <>
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="lg:grid lg:grid-cols-12 lg:gap-x-16">
          <div className="lg:col-span-5 xl:col-span-4">
            <Calendar
              onSelectDate={setSelectedDate}
              onMonthChange={setCalendarMonth}
              dayCounts={calendarSummary?.days || []}
            />
          </div>
          <div className="lg:col-span-7 xl:col-span-8">
            <div className="flex items-center justify-between mb-4 mt-4 lg:mt-0">
              <h2 className="text-base font-semibold text-ink-900">
                {getScheduleHeading()}
              </h2>
              {!isLoadingAppointments && appointmentCount > 0 && (
                <button
                  type="button"
                  onClick={onCreateHandler}
                  className="inline-flex items-center gap-x-1.5 rounded-md bg-accent-500 px-3 py-2 text-sm font-semibold text-white hover:bg-accent-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-500"
                >
                  <PlusIcon className="size-4 -ml-0.5" />
                  New Appointment
                </button>
              )}
            </div>
            {!isLoadingAppointments && appointmentCount > 0 && (
              <p className="text-sm text-ink-500 -mt-2 mb-4">
                {appointmentCount} appointment{appointmentCount !== 1 ? "s" : ""}
                {dailyRevenue > 0 && (
                  <> &middot; {formatPriceWithCurrency(dailyRevenue, currency)} revenue</>
                )}
              </p>
            )}
            {isLoadingAppointments ? (
              <div className="animate-pulse">
                {/* Appointment list skeleton */}
                <div className="mt-4 divide-y divide-ink-100">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="relative flex gap-x-6 py-6">
                      <div className="flex-auto min-w-0">
                        <div className="flex items-start gap-x-3">
                          <div className="h-4 w-32 bg-cream-300 rounded"></div>
                          <div className="h-5 w-16 bg-cream-300 rounded"></div>
                        </div>
                        <div className="mt-2 flex flex-col gap-2">
                          <div className="flex items-start gap-x-3">
                            <div className="h-5 w-5 bg-cream-300 rounded"></div>
                            <div className="h-4 w-40 bg-cream-300 rounded"></div>
                          </div>
                          <div className="flex items-start gap-x-3">
                            <div className="h-5 w-5 bg-cream-300 rounded"></div>
                            <div className="h-4 w-48 bg-cream-300 rounded"></div>
                          </div>
                        </div>
                      </div>
                      <div className="absolute right-0 top-6 xl:relative xl:right-auto xl:top-auto xl:self-center">
                        <div className="h-8 w-8 bg-cream-300 rounded-full"></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : !appointmentsData?.appointments ||
              appointmentsData.appointments.length === 0 ? (
              <div className="py-8">
                <EmptyState
                  text="No appointments scheduled"
                  subText="This day is wide open — create an appointment to fill it."
                  buttonText="New Appointment"
                  onClick={onCreateHandler}
                />
              </div>
            ) : (
              <AppointmentList
                appointments={appointmentsData.appointments}
                onView={onViewHandler}
                onEdit={onEditHandler}
                onCancel={onCancelHandler}
                onClientClick={onClientClickHandler}
              />
            )}
          </div>
        </div>
      </div>

      {showAppointmentDrawer && (
        <Modal
          open={showAppointmentDrawer}
          onClose={closeDrawerHandler}
          title={appointmentModalTitle}
          size="lg"
          confirmClose={isFormDirty}
          closeRef={modalCloseRef}
        >
          <AppointmentForm
            closeDrawerHandler={closeDrawerHandler}
            defaultAppointment={selectedAppointment}
            defaultClient={
              selectedAppointment
                ? {
                    _id: selectedAppointment.clientId,
                    fullName: selectedAppointment.clientFullName,
                  }
                : undefined
            }
            mode={appointmentFormMode}
            onCancelHandler={() => modalCloseRef.current?.()}
            setIsFormDirty={setIsFormDirty}
            saveAppointment={mutateSaveAppointment}
            patchAppointment={mutatePatchAppointment}
            selectedDate={selectedDate}
            onModalTitleChange={setAppointmentModalTitle}
          />
        </Modal>
      )}

      {showAppointmentView && (
        <Modal
          open={showAppointmentView}
          onClose={closeDrawerHandler}
          title="Appointment Details"
          size="sm"
        >
          <ViewAppointment
            appointment={selectedAppointment}
            clientName={selectedAppointment?.clientFullName}
          />
        </Modal>
      )}

      {showCancelDrawer && (
        <Modal
          open={showCancelDrawer}
          onClose={closeDrawerHandler}
          title="Cancel Appointment"
          size="sm"
          confirmClose={isFormDirty}
          closeRef={modalCloseRef}
        >
          <CancelAppointmentForm
            appointment={selectedAppointment}
            closeDrawerHandler={closeDrawerHandler}
            onCancelHandler={() => modalCloseRef.current?.()}
            setIsFormDirty={setIsFormDirty}
            patchAppointment={mutatePatchAppointment}
          />
        </Modal>
      )}
    </>
  );
};

export default CalendarPage;
